import { useContext, useEffect, useRef } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { AppContexts } from "../../providers";
import { FEATURES, getFeature } from "./catalog";
import { getUsage, useFeatureUsage } from "./usage";
import { FeatureAction, SubscriptionRequirement } from "./shared";
import { CATEGORIES, readFilters, matchesFeature } from "./filters";
import "./features.css";
import { getLocalGuideUrl } from "../../content/help/catalog";

const FeatureUsage = ({ feature, metrics, loading }) => {
  const usage = getUsage(feature, metrics);
  if (!loading && feature.metric && usage.state === "unknown") return null;
  return (
    <div className={`rbr-feature-usage is-${usage.state}`}>
      <strong>{!loading && usage.state === "active" && <span aria-hidden="true">✓ </span>}{loading && feature.metric ? "Checking activity…" : usage.label}</strong>
      <span>{loading && feature.metric ? (feature.usageBasis === "setting" ? "Checking Guide Designer settings." : feature.usageBasis === "current" ? `Counting your ${feature.metricLabel}.` : "Looking for recent team activity.") : usage.detail}</span>
      {feature.scope === "personal" && <span>Personal feature — configured separately for each user.</span>}
    </div>
  );
};

const FeatureDiscovery = () => {
  const { user: account } = useContext(AppContexts.AuthContext);
  const { featureId } = useParams();
  const selected = getFeature(featureId);
  const history = useHistory();
  const location = useLocation();
  const headingRef = useRef(null);
  const { query, view, category } = readFilters(location.search);
  const updateFilters = (changes) => {
    const params = new URLSearchParams(location.search);
    params.delete("requirement");
    Object.entries(changes).forEach(([key, value]) => {
      if (!value || (key !== "q" && value === "all")) params.delete(key);
      else params.set(key, value);
    });
    history.replace({ ...location, search: params.toString() ? `?${params}` : "" });
  };
  const featureUrl = (id) => `/features/${id}${location.search}`;
  const { metrics, loading } = useFeatureUsage(account?.account_id);

  useEffect(() => {
    document.title = `${selected?.title || "Your Features"} - RueBaRue`;
  }, [selected]);

  useEffect(() => {
    if (featureId) headingRef.current?.focus();
  }, [featureId]);

  const visible = FEATURES.filter((feature) => matchesFeature(
    feature, { query, view, category }, metrics,
  ));
  const showLoading = loading && view !== "all";


  return (
    <section className="rbr-features-page">
      <div id="hms-page-title"><h1 className="uk-heading-small" ref={headingRef} tabIndex={-1}>{selected?.title || "Your Features"}</h1></div>
      <div id="hms-main-body">
        {!featureId && <div className="rbr-discovery-intro">
          <div>
            <p>Discover what your team can do, find instructions, and take the next step.</p>
          </div>
          <Link className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" to="/whats-new">See what’s new →</Link>
        </div>}
        {featureId ? (
          <>
            <Link className="rbr-feature-back" to={`/features${location.search}`}>← Back to features</Link>
            {selected ? (
              <article className="rbr-feature-card rbr-feature-detail">
                <span className="rbr-discovery-eyebrow">{selected.category}</span>
                <p className="rbr-feature-benefit">{selected.description}</p>
                <SubscriptionRequirement feature={selected} />
                <FeatureUsage feature={selected} metrics={metrics} loading={loading} />
                <h2>How to use {selected.title}</h2>
                <ol className="rbr-feature-steps">{selected.steps.map((step) => <li key={step}>{step}</li>)}</ol>
                <div className="hms-guest-btn">
                  <FeatureAction feature={selected} account={account} />
                  {selected.learnMoreUrl && <a className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" href={selected.learnMoreUrl} target="_blank" rel="noopener noreferrer">Learn more ↗</a>}
                  {getLocalGuideUrl(selected.helpUrl) ? <Link className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" to={getLocalGuideUrl(selected.helpUrl)}>Read the full guide</Link> : selected.helpUrl && <a className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" href={selected.helpUrl} target="_blank" rel="noopener noreferrer">Read the full guide ↗</a>}
                </div>
                {selected.requirement === "confirm" && <p><a href="mailto:support@ruebarue.com">Contact support</a> to confirm the subscription required for this feature.</p>}
              </article>
            ) : <p role="status">This feature was not found. Browse all features to find its instructions.</p>}
          </>
        ) : (
          <>
            <div className="rbr-feature-filters">
              <div>
                <label className="uk-form-label" htmlFor="feature-search">Search features</label>
                <div className="uk-search uk-search-default">
                  {query ? <button type="button" className="uk-form-icon-flip uk-form-icon" aria-label="Clear feature search" onClick={() => updateFilters({ q: "" })} data-uk-icon="icon: close" /> : <span className="uk-search-icon-flip" data-uk-search-icon="true" aria-hidden="true" />}
                  <input id="feature-search" type="search" className="uk-search-input" placeholder="Search by feature or benefit" value={query} onChange={(event) => updateFilters({ q: event.target.value })} />
                </div>
              </div>
              <div>
                <label className="uk-form-label" htmlFor="feature-category">Category</label>
                <select id="feature-category" className="uk-select" value={category} onChange={(event) => updateFilters({ category: event.target.value })}><option value="all">All categories</option>{CATEGORIES.map((name) => <option key={name} value={name}>{name}</option>)}</select>
              </div>
            </div>
            <div className="rbr-feature-views rbr-pill-toggle" role="group" aria-label="Feature views">
              {[ ["all", "All features"], ["active", "Features you use"], ["explore", "Features to explore"] ].map(([value, label]) => (
                <button key={value} type="button" className={`rbr-pill-toggle-button${view === value ? " is-active" : ""}`} aria-pressed={view === value} onClick={() => updateFilters({ view: value })}>{label}</button>
              ))}
            </div>
            <p className="rbr-discovery-muted" role="status">{loading ? "Checking recent activity…" : `${visible.length} ${visible.length === 1 ? "feature" : "features"}`}</p>
            <div className="rbr-feature-grid">
              {!showLoading && visible.map((feature) => (
                <article key={feature.id} className="rbr-feature-card">
                  <span className="rbr-discovery-eyebrow">{feature.category}</span>
                  <h2><Link to={featureUrl(feature.id)}>{feature.title}</Link></h2>
                  <p className="rbr-feature-benefit">{feature.description}</p>
                  <SubscriptionRequirement feature={feature} />
                  <FeatureUsage feature={feature} metrics={metrics} loading={loading} />
                  <div className="hms-guest-btn">
                    <FeatureAction feature={feature} account={account} />
                    <Link className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" to={featureUrl(feature.id)}>How to use</Link>
                  </div>
                </article>
              ))}
            </div>
            {!showLoading && !visible.length && <div className="rbr-feature-empty"><h2>{view === "active" ? "No matching features with recorded activity" : "No matching features"}</h2><p>{view === "active" ? "Your team may use features we do not track yet. Browse all features for instructions." : "Try a different search or category."}</p><button type="button" className="uk-button hms-btn hms-white-btn" onClick={() => updateFilters({ q: "", category: "all", view: "all" })}>Show all features</button></div>}
            <p className="secondary uk-margin-small-top">Ask Guide AI and AI Trip Planner reflect their Guide Designer settings. Activity counts cover the last 12 months. Area Guide, Extras, Contacts, Emergency Alerts, Saved Responses, Task Management, and Inventory Management use current account records. Shared Team Inbox includes conversations with messages, including archived conversations. Text Automation uses active scheduled messages. Features to explore also includes features whose usage is not tracked yet.</p>
          </>
        )}
      </div>
    </section>
  );
};

export default FeatureDiscovery;
